/**
 * Styring af "Hero med flere videoer" (sections/index-hero-slider.liquid).
 *
 * Én slide: videoen kører i loop (sat i HTML'en), og der er ingen indikator. Her sørger scriptet kun for at starte
 * videoen, hvis browseren har blokeret autoplay, og for at stoppe den ved "Reducer bevægelse".
 *
 * Flere slides:
 *  - Aktiv slide vises, de andre er skjult. Slides med video skifter, når videoen er slut. Slides uden video skifter
 *    efter data-duration sekunder.
 *  - Indikatoren (én streg pr. slide) fyldes op i takt med videoen/tiden. Streger før den aktive er fyldt.
 *  - Klik på en streg hopper til den slide. Efter den sidste starter den første igen. Swipe til siden virker også.
 *  - Videoen i næste slide hentes først, når den nuværende er begyndt at spille (så den ikke konkurrerer om båndbredden).
 *  - Alt pauses, når heroen er ude af syne eller fanen er skjult. Ved "Reducer bevægelse" afspilles ingen video,
 *    og der skiftes ikke automatisk (indikatoren kan stadig bruges).
 *  - Temaeditoren: når en slide vælges, vises den og auto-skift holdes.
 */
(function () {
  if (window.__ttHeroSliderLoaded) return;
  window.__ttHeroSliderLoaded = true;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var mobileQuery = window.matchMedia('(max-width: 999px)');

  function isMobile() {
    return mobileQuery.matches;
  }

  /* Sætter video-kilden (kun hvis den ikke allerede er sat) og starter hentningen. */
  function ensureSource(video) {
    if (!video || video.currentSrc || video.getAttribute('src')) return;
    var desktop = video.getAttribute('data-src-desktop');
    var mobile = video.getAttribute('data-src-mobile');
    var url = isMobile() ? (mobile || desktop) : (desktop || mobile);
    if (!url) return;
    video.setAttribute('src', url);
    video.preload = 'auto';
    video.load();
  }

  function tryPlay(video) {
    if (!video || reduceMotion.matches) return;
    var p = video.play();
    if (p && typeof p.catch === 'function') p.catch(function () {});
  }

  function HeroSlider(root) {
    this.root = root;
    this.slides = Array.prototype.slice.call(root.querySelectorAll('[data-slide]'));
    this.bars = Array.prototype.slice.call(root.querySelectorAll('[data-bar]'));
    this.fills = Array.prototype.slice.call(root.querySelectorAll('[data-bar-fill]'));
    this.count = this.slides.length;
    this.multi = this.count > 1;
    this.autoplay = root.getAttribute('data-autoplay') !== 'false';
    this.index = 0;
    this.visible = true;
    this.editorHold = false;
    this.raf = null;
    this.timerStart = 0;
    this.timerElapsed = 0;
    this.destroyed = false;
    this.shown = 0; /* den viste fyldning på den aktive streg (blødgjort, så den ikke hakker) */
    this.lastNow = 0;
    this.listeners = [];
    this.setup();
  }

  HeroSlider.prototype.on = function (target, type, fn, opts) {
    target.addEventListener(type, fn, opts);
    this.listeners.push([target, type, fn, opts]);
  };

  HeroSlider.prototype.setup = function () {
    var self = this;
    var firstVideo = this.slides[0] && this.slides[0].querySelector('.hs-video');

    /* Start/stop-hjælp gælder både for én og flere slides */
    function startOnInteraction() {
      var v = self.activeVideo();
      if (v && v.paused) tryPlay(v);
    }
    ['touchstart', 'click', 'scroll'].forEach(function (type) {
      self.on(window, type, startOnInteraction, { once: true, passive: true });
    });

    this.on(reduceMotion, 'change', function () { self.applyMotionPreference(); });
    this.applyMotionPreference();

    if (!this.multi) {
      if (firstVideo) {
        if (reduceMotion.matches) firstVideo.pause();
        else if (firstVideo.paused) tryPlay(firstVideo);
      }
      return;
    }

    /* Flere slides */
    this.bars.forEach(function (bar, i) {
      self.on(bar, 'click', function () { self.goTo(i); });
    });

    this.slides.forEach(function (slide, i) {
      var video = slide.querySelector('.hs-video');
      if (video) {
        /* Uden auto-skift gentages videoen i stedet for at stå stille på sidste billede */
        if (!self.autoplay) video.loop = true;
        self.on(video, 'ended', function () {
          if (i !== self.index) return;
          if (self.canAdvance()) {
            self.next();
          } else {
            video.currentTime = 0;
            tryPlay(video);
          }
        });
        /* Hvis videoen ikke kan hentes eller afspilles, skifter vi efter slidets varighed i stedet for at hænge */
        var failed = function () { if (i === self.index) self.startTimer(); };
        self.on(video, 'error', failed);
        var sources = video.querySelectorAll('source');
        if (sources.length) self.on(sources[sources.length - 1], 'error', failed);
        self.on(video, 'playing', function () {
          if (i !== self.index) return;
          self.timerMode = false;
          self.preloadNext();
        });
      }
    });

    /* Pause, når heroen ikke er synlig, eller fanen er skjult */
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(function (entries) {
        self.visible = entries[0].isIntersecting;
        self.updatePlayback();
      }, { threshold: 0.25 });
      this.observer.observe(this.root);
    }
    this.on(document, 'visibilitychange', function () { self.updatePlayback(); });

    /* Swipe på touch-skærme */
    var touchX = null;
    this.on(this.root, 'touchstart', function (e) {
      touchX = e.touches.length === 1 ? e.touches[0].clientX : null;
    }, { passive: true });
    this.on(this.root, 'touchend', function (e) {
      if (touchX === null) return;
      var dx = e.changedTouches[0].clientX - touchX;
      touchX = null;
      if (Math.abs(dx) > 50) {
        if (dx < 0) self.next(); else self.prev();
      }
    }, { passive: true });

    /* Temaeditoren */
    this.on(this.root, 'shopify:block:select', function (e) {
      var i = self.slides.indexOf(e.target);
      if (i > -1) {
        self.editorHold = true;
        self.goTo(i);
      }
    });
    this.on(this.root, 'shopify:block:deselect', function () {
      self.editorHold = false;
      self.updatePlayback();
    });

    this.activate(0, true);
    this.loop();
  };

  HeroSlider.prototype.applyMotionPreference = function () {
    if (reduceMotion.matches) {
      this.root.classList.add('is-reduced-motion');
      this.slides.forEach(function (slide) {
        var v = slide.querySelector('.hs-video');
        if (v) v.pause();
      });
    } else {
      this.root.classList.remove('is-reduced-motion');
      var v = this.activeVideo();
      if (v && v.paused) tryPlay(v);
    }
  };

  HeroSlider.prototype.activeVideo = function () {
    var slide = this.slides[this.index];
    return slide ? slide.querySelector('.hs-video') : null;
  };

  HeroSlider.prototype.hasVideo = function (slide) {
    return !!(slide && slide.querySelector('.hs-video'));
  };

  /* Må der afspilles/tælles? (synlig, fanen aktiv, ikke "reducer bevægelse") */
  HeroSlider.prototype.shouldRun = function () {
    return this.visible && !document.hidden && !reduceMotion.matches;
  };

  /* Må der skiftes automatisk til næste slide? */
  HeroSlider.prototype.canAdvance = function () {
    return this.shouldRun() && this.autoplay && !this.editorHold;
  };

  HeroSlider.prototype.setFill = function (i, p) {
    /* Tallet sættes på selve knappen, så både den hvide fyldning og bladet kan læse det (CSS-variabler nedarves) */
    var holder = this.bars[i] || this.fills[i];
    if (holder) holder.style.setProperty('--hs-progress', String(Math.max(0, Math.min(1, p))));
  };

  HeroSlider.prototype.activate = function (i, initial) {
    var self = this;
    this.index = i;
    this.timerMode = false;
    this.timerElapsed = 0;
    this.timerStart = performance.now();
    this.activatedAt = this.timerStart;

    this.slides.forEach(function (slide, j) {
      var active = j === i;
      slide.classList.toggle('is-active', active);
      if (active) {
        slide.removeAttribute('inert');
        slide.removeAttribute('aria-hidden');
      } else {
        slide.setAttribute('inert', '');
        slide.setAttribute('aria-hidden', 'true');
      }
      var v = slide.querySelector('.hs-video');
      if (v && !active) v.pause();
    });

    this.shown = 0;

    /* Med bladet er det kun den aktive streg, der har hvid fyldning (sammen med bladet). Streger, der ikke er aktive, er mørke.
       Den streg, der netop var aktiv, beholder sit tal i et øjeblik, så fyldning og blad toner ud på stedet (og ikke ruller tilbage). */
    var leafMode = this.root.classList.contains('hs-has-leaf');
    this.bars.forEach(function (bar, j) {
      var wasActive = bar.classList.contains('is-active');
      clearTimeout(bar._hsFade);
      /* Ny aktiv streg: fyldningen vokser blødt ud fra nul, mens bladet toner ind (is-starting, 0,6 sek.) */
      if (!initial && j === i && !wasActive && leafMode) {
        bar.classList.add('is-starting');
        clearTimeout(bar._hsStart);
        bar._hsStart = setTimeout(function () { bar.classList.remove('is-starting'); }, 600);
      }
      bar.classList.toggle('is-active', j === i);
      bar.classList.toggle('is-past', j < i);
      if (j === i) bar.setAttribute('aria-current', 'true');
      else bar.removeAttribute('aria-current');
      if (leafMode && wasActive && j !== i) {
        bar._hsFade = setTimeout(function () { self.setFill(j, j < self.index ? 1 : 0); }, 450);
      } else {
        self.setFill(j, j < i ? 1 : 0);
      }
    });

    var slide = this.slides[i];
    var video = slide.querySelector('.hs-video');
    if (video) {
      ensureSource(video);
      /* Første slide spiller allerede fra HTML'en - den skal ikke nulstilles ved start */
      if (!initial) {
        try { video.currentTime = 0; } catch (err) { /* metadata ikke klar endnu */ }
        if (this.shouldRun()) tryPlay(video);
      }
    } else {
      this.timerMode = true;
      this.preloadNext();
    }
  };

  /* Slide uden video, eller video der ikke kan afspilles: skift efter varigheden. */
  HeroSlider.prototype.startTimer = function () {
    this.timerMode = true;
    this.timerElapsed = 0;
    this.timerStart = performance.now();
  };

  HeroSlider.prototype.preloadNext = function () {
    if (this.count < 2) return;
    var next = this.slides[(this.index + 1) % this.count];
    ensureSource(next && next.querySelector('.hs-video'));
  };

  /* Klik på en streg (også den aktive): vis den slide og start forfra */
HeroSlider.prototype.goTo = function (i) {
  this.activate(i);
};

HeroSlider.prototype.next = function () {
    this.activate((this.index + 1) % this.count);
  };

  HeroSlider.prototype.prev = function () {
    this.activate((this.index - 1 + this.count) % this.count);
  };

  HeroSlider.prototype.updatePlayback = function () {
  var v = this.activeVideo();
  if (!v) return;
  if (this.shouldRun()) {
    if (v.paused && !v.ended) tryPlay(v);
  } else if (!v.paused) {
    v.pause();
  }
};

/* Én animationsløkke opdaterer den aktive streg og skifter slides uden video. */
HeroSlider.prototype.loop = function () {
  var self = this;
  function tick(now) {
    if (self.destroyed) return;
    var slide = self.slides[self.index];
    var video = slide && slide.querySelector('.hs-video');
    var running = self.shouldRun();

    if (video && !self.timerMode) {
      /* Slide med video: stregen følger videoens afspilning */
      if (isFinite(video.duration) && video.duration > 0) {
        /* Videoens tid opdateres i små ryk (især i Safari). Fyldningen glider derfor mod målet med en kort udjævning (ca. 0,1 sek.), så stregen og bladet bevæger sig jævnt. */
        var target = video.currentTime / video.duration;
        var dt = self.lastNow ? Math.min(now - self.lastNow, 250) : 16;
        if (target < self.shown - 0.02 || reduceMotion.matches) self.shown = target;
        else self.shown += (target - self.shown) * (1 - Math.exp(-dt / 90));
        self.setFill(self.index, self.shown);
      } else {
        self.setFill(self.index, 0);
      }
      /* Sikkerhedsnet: hvis videoen slet ikke kommer i gang (fx blokeret autoplay), skifter vi efter varigheden */
      if (running && video.paused && !video.ended && now - self.activatedAt > 5000) {
        self.startTimer();
      }
    } else {
      /* Slide uden video (eller video der ikke virker): stregen følger tiden */
      var duration = (parseFloat(slide.getAttribute('data-duration')) || 6) * 1000;
      if (running) {
        self.timerElapsed = now - self.timerStart;
      } else {
        self.timerStart = now - self.timerElapsed;
      }
      self.setFill(self.index, self.timerElapsed / duration);
      if (self.canAdvance() && self.timerElapsed >= duration) {
        self.next();
      }
    }
    self.lastNow = now;
    self.raf = window.requestAnimationFrame(tick);
  }
  this.raf = window.requestAnimationFrame(tick);
};

HeroSlider.prototype.destroy = function () {
    this.destroyed = true;
    if (this.raf) window.cancelAnimationFrame(this.raf);
    if (this.observer) this.observer.disconnect();
    this.listeners.forEach(function (l) { l[0].removeEventListener(l[1], l[2], l[3]); });
    this.listeners = [];
  };

  function initAll() {
    document.querySelectorAll('[data-hero-slider]').forEach(function (el) {
      if (el.__heroSlider) return;
      el.__heroSlider = new HeroSlider(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:section:unload', function (e) {
    var el = e.target && e.target.querySelector && e.target.querySelector('[data-hero-slider]');
    if (el && el.__heroSlider) {
      el.__heroSlider.destroy();
      el.__heroSlider = null;
    }
  });
})();
